<template>
  <ReportPageLayout>
    <template #page-header>
      <OverviewNavHeader />
    </template>

    <!-- Default slot for main content -->
    <DataStateHandler
      :loading="loading"
      :error="error"
      :isEmpty="!report && !loading && !error"
    >
      <!-- Custom error state with Retry button -->
      <template #error="{ errorMessage }">
        <div class="error-container text-center py-8">
          <v-alert
            type="error"
            title="Error Loading Report"
            :text="errorMessage"
            class="mx-auto"
            max-width="600"
            variant="tonal"
            border="start"
          ></v-alert>
          <v-btn color="primary" class="mt-4" @click="reloadReport">
            Retry
          </v-btn>
        </div>
      </template>

      <!-- Custom empty state -->
      <template #empty>
        <div class="text-center py-8">
          <v-alert
            type="info"
            title="No Report Data"
            text="No report data was found."
            class="mx-auto"
            max-width="600"
            variant="tonal"
          ></v-alert>
        </div>
      </template>

      <!-- Default Content (report loaded) -->
      <div v-if="report">
        <!-- Ensure report exists before rendering content -->
        <v-row>
          <v-col cols="12">
            <OverviewHeadingCard :report="report" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3">
            <OverviewSideNav />
          </v-col>
          <v-col cols="12" md="9">
            <div class="reports-overview-section">
              <div class="mb-4 text-secondary content-section-title">
                Report Overview
              </div>
              <SeriesPerformanceSnapshot />

              <div class="mt-6">
                <div>
                  <SectionHeading title="ELA K-2 Overview" level="h2">
                    <template #actions>
                      <v-btn
                        variant="text"
                        color="primary"
                        class="text-none"
                        density="comfortable"
                        href="#"
                      >
                        Optional Link
                      </v-btn>
                    </template>
                  </SectionHeading>

                  <v-expand-transition>
                    <div>
                      <ReadMore>
                        The instructional materials reviewed for Grades K-2 meet
                        expectations for Alignment to NGSS, Gateways 1 and 2.
                        Gateway 1: Designed for NGSS; Criterion 1:
                        Three-Dimensional Le arning meets expectations. The
                        materials include three-dimensional learning
                        opportunities and opportunities for student sensemaking
                        with the three dimensions. The formative and summative
                        assessments consistently measure the three dimensions
                        for their respective objectives. Criterion 2: Phenomena
                        and Problems Drive Learning meets expectations.
                        Phenomena and problems are present, connected to DCIs,
                        and presented to students as directly as possible. The
                        materials consistently elicit but inconsistently
                        leverage student prior knowledge and experience related
                        to the phenomena and problems present. Phenomena and
                        problems consistently drive learning. The instructional
                        materials reviewed for Grades K-2 meet expectations for
                        Alignment to NGSS, Gateways 1 and 2. Gateway 1: Designed
                        for NGSS; Criterion 1: Three-Dimensional Le arning meets
                        expectations. The materials include three-dimensional
                        learning opportunities and opportunities for student
                        sensemaking with the three dimensions. The formative and
                        summative assessments consistently measure the three
                        dimensions for their respective objectives. Criterion 2:
                        Phenomena and Problems Drive Learning meets
                        expectations. Phenomena and problems are present,
                        connected to DCIs, and presented to students as directly
                        as possible. The materials consistently elicit but
                        inconsistently leverage student prior knowledge and
                        experience related to the phenomena and problems
                        present. Phenomena and problems consistently drive
                        learning.
                      </ReadMore>
                    </div>
                  </v-expand-transition>

                  <v-divider class="my-12"></v-divider>

                  <SummaryAlignmentUsability :grades="summaryGrades" />

                  <v-divider class="my-12"></v-divider>

                  <SectionHeading
                    title="Science of Reading Snapshot"
                    level="h2"
                    titleClass="text-h6 font-weight-bold text-secondary mb-8"
                  >
                    <template #subtitle>
                      <a
                        class="text-primary text-decoration-underline section-subtitle-link"
                        @click.prevent="openGlossary('Science of Reading')"
                      >
                        Science of Reading
                      </a>
                      Snapshot
                    </template>
                  </SectionHeading>

                  <h3 class="font-weight-bold text-secondary mb-2">
                    What are
                    <a
                      class="text-primary text-decoration-underline"
                      @click.prevent="openGlossary('non-negotiables')"
                    >
                      non-negotiables </a
                    >?
                  </h3>
                  <div class="content">
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                    Phasellus pulvinar imperdiet semper. Aliquam tincidunt, orci
                    ac feugiat efficitur, sem sapien sagittis dolor, euismod
                    ultrices metus quam sit amet lectus. Etiam mollis sit amet
                    lectus et pharetra. Sed laoreet at enim nec tincidunt. Cras
                    varius sem ac urna convallis, eu bibendum orci dapibus.
                  </div>

                  <!-- Science of Reading Snapshot Table -->
                  <DataTable
                    :headers="['K', '1', '2']"
                    :items="readingSnapshotData"
                    :skillName="true"
                    classes="mt-6"
                  />

                  <SectionHeading
                    title="Additional Criteria (example shows K-2, see purple lists for other criteria possibilities)"
                    level="h3"
                    titleClass="font-weight-bold text-secondary my-16"
                  />

                  <!-- Additional Criteria Table -->
                  <DataTable
                    :headers="['K', '1', '2']"
                    :items="additionalCriteriaData"
                    :skillName="true"
                    classes="mb-16"
                  />

                  <SectionHeading
                    title="ELA 3-6 Overview"
                    level="h2"
                    titleClass="text-secondary mb-8"
                  />
                  <div class="content mb-16">
                    <p>
                      View Literacy materials for Grades K-2 meet expectations
                      for alignment and usability. The materials include a broad
                      variety and range of high-quality texts, strong daily
                      opportunities for reading, writing, speaking, and
                      listening aligned to the standards.
                    </p>
                    <p>
                      There is also practice of phonics, fluency, and word
                      recognition and analysis skills.
                    </p>
                    <p>
                      Questions and tasks support students as they engage with
                      texts and build literacy skills. Information for
                      implementation supports teachers as materials are used in
                      the classroom, including assessments, standards
                      alignments, and supports for all learners.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </DataStateHandler>
  </ReportPageLayout>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue';
  import { useRoute } from 'vue-router';
  import { API_BASE_URL } from '@/config';

  // Composables
  import { useDocumentTitle } from '../composables/useDocumentTitle';
  import { useGlossary } from '../composables/useGlossary';
  import { useReportLoader } from '../composables/useReportLoader';

  // Layout component
  import ReportPageLayout from '../layouts/ReportPageLayout.vue';

  // Shared components
  import DataStateHandler from '../components/shared/DataStateHandler.vue';
  import SectionHeading from '../components/shared/SectionHeading.vue';
  import ReadMore from '../components/shared/ReadMore.vue';
  import DataTable from '../components/shared/DataTable.vue';

  // Feature components
  import SeriesPerformanceSnapshot from '../components/reports/SeriesPerformanceSnapshot.vue';
  import SummaryAlignmentUsability from '../components/reports/SummaryAlignmentUsability.vue';
  import OverviewNavHeader from '../components/reportOverview/OverviewNavHeader.vue';
  import OverviewSideNav from '../components/reportOverview/OverviewSideNav.vue';
  import OverviewHeadingCard from '../components/reportOverview/OverviewHeadingCard.vue';

  const route = useRoute();
  const { openGlossary } = useGlossary();

  const reportSlug = computed(() => route.params.id as string);
  const { report, loading, error, reloadReport } = useReportLoader(reportSlug);

  // Mock data (kept for now)
  const summaryGrades = computed(() => [
    {
      name: 'Kindergarten',
      gateways: [
        {
          name: 'Gateway 1',
          description: 'Standards & Research-Based Practices',
          score: '91%',
          status: 'Meets Expectations',
        },
        {
          name: 'Gateway 2',
          description: 'Standards & Research-Based Practices',
          score: '91%',
          status: 'Partially Meets Expectations',
        },
        {
          name: 'Gateway 3',
          description: 'Standards & Research-Based Practices',
          score: 'NE',
          status: 'Not Eligible',
        },
      ],
    },
    {
      name: '1st Grade',
      gateways: [
        {
          name: 'Gateway 1',
          description: 'Standards & Research-Based Practices',
          score: '41%',
          status: 'Does not Meet Expectations',
        },
        {
          name: 'Gateway 2',
          description: 'Standards & Research-Based Practices',
          score: 'NE',
          status: 'Not Eligible',
        },
        {
          name: 'Gateway 3',
          description: 'Standards & Research-Based Practices',
          score: 'NE',
          status: 'Not Eligible',
        },
      ],
    },
    {
      name: 'High School',
      gateways: [
        {
          name: 'Gateway 1',
          description: 'Standards & Research-Based Practices',
          score: '91%',
          status: 'Meets Expectations',
        },
        {
          name: 'Gateway 2',
          description: 'Standards & Research-Based Practices',
          score: '91%',
          status: 'Meets Expectations',
        },
        {
          name: 'Gateway 3',
          description: 'Standards & Research-Based Practices',
          score: 'NE',
          status: 'Not Eligible',
        },
      ],
    },
  ]);

  // Data for the Science of Reading table
  const readingSnapshotData = ref([
    {
      skill: 'Phonemic Awareness',
      subtitle: 'Non-negotiable',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Phonics',
      subtitle: 'Non-negotiable',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Fluency',
      subtitle: 'Non-negotiable',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Vocabulary',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Comprehension',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
  ]);

  // Data for the Additional Criteria table
  const additionalCriteriaData = ref([
    {
      skill: 'Print Concepts',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Word Recognition',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Text Quality & Complexity',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Tasks & Questions',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Foundational Skills',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Building Knowledge',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Coherence',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Scope & Sequence',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Decodable Texts',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Teacher Supports',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Assessments',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Student Supports',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
    {
      skill: 'Intentional Design',
      k: '100%',
      grade1: '100%',
      grade2: '100%',
    },
  ]);

  const documentTitleState = computed(() => {
    if (loading.value) {
      return 'Loading Report...';
    } else if (error.value) {
      return 'Error Loading Report';
    } else if (report.value && report.value.title) {
      return report.value.title;
    } else {
      return 'Report Overview';
    }
  });
  useDocumentTitle(documentTitleState);
</script>

<style lang="scss">
  @use '../styles/abstracts/variables' as *;

  .v-responsive {
    padding-right: 0;
  }

  .content-section-title {
    font-family: 'rockwell', sans-serif !important;
    font-size: 38px !important;
    font-weight: normal !important;
  }

  .overview-section {
  }

  .performance-snapshot {
    background: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .performance-table {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;

    :deep(th) {
      font-weight: 600;
      color: rgba(0, 0, 0, 0.87);
      background: #f5f5f5;
    }

    :deep(td) {
      height: 48px;
    }
  }

  .data-table {
    thead th {
      border-bottom: 1px solid
        rgba(var(--v-border-color), var(--v-border-opacity));
      font-weight: $font-weight-bold !important;
      color: rgb(var(--v-theme-tonal-black));
      font-size: 18px;
      padding-top: 12px;
      padding-bottom: 12px;
      background-color: white;
    }

    thead th:first-child {
      padding: 0;
    }

    tbody {
      tr {
        background-color: white;

        &:nth-child(even) {
          background-color: #f0f3f5;
        }

        td {
          border-bottom: 1px solid
            rgba(var(--v-border-color), var(--v-border-opacity));
          vertical-align: middle;
          padding: 16px !important;
          font-size: 18px;
          color: rgb(var(--v-theme-text-secondary));
        }

        &:last-child td {
          border-bottom: none;
        }

        td:first-child {
          padding-left: 0;
          padding-right: 16px;
        }
      }
    }

    .skill-name {
      color: rgb(var(--v-theme-text-primary));
      font-size: 18px;
      margin-bottom: 2px;
    }

    .skill-subtitle {
      color: rgb(var(--v-theme-text-secondary));
      font-size: 14px;
    }

    .grade-header {
      padding-right: 16px;
    }
  }
</style>
